import React, { PureComponent } from 'react';
import { withRouter } from 'react-router-dom';
import classNames from 'classnames';
import { IntlProps, addTranslation } from 'decorators/addTranslation';
import { WithRouterProps } from 'decorators/withRouter';
import Mapper from 'helpers/Mapper';
import dataMapper from './operationItemDataMapper';
import { AnyObject } from 'types/Common';
import Checkbox from '../../ui/checkbox';
import ConditionalWrapper from '../../ui/conditionalWrapper';
import CopyToClipboard from '../../ui/copyToClipboard';
import LabelStatus from 'components/ui/labelStatus';

interface OwnProps {
  data: AnyObject;
  isCurrent: boolean;
  onSelect: (operationId: string) => void;
  onCheckboxChange?: (id: string, isSelected: boolean) => void;
  onStatusClick?: (parentOperationId: string) => void;
  customClass?: string;
  isDisabled?: boolean;
  isCheckboxDisabled?: boolean;
  type?: 'chargeback' | 'operation';
}

type Props = OwnProps & IntlProps & WithRouterProps;

class OperationItem extends PureComponent<Props> {
  static defaultProps = {
    type: 'operation' as const,
  };

  render() {
    const {
      data,
      isCurrent,
      onSelect,
      type,
      isDisabled,
      isCheckboxDisabled,
      customClass,
      onCheckboxChange,
      onStatusClick,
    } = this.props;
    const {
      id,
      label,
      value,
      isSelected,
      operationType,
      updatedDate,
      amount,
      chargeback,
      currency,
      fraud,
      status,
      recurring,
    } = dataMapper(data, type);
    const isChargeback = type === 'chargeback';

    return (
      <div
        className={classNames(
          'border-border-base cursor-pointer border-b px-5 py-4 last:border-none',
          {
            'bg-background-secondary after:bg-brand-main relative after:absolute after:top-0 after:left-0 after:h-full after:w-1':
              isCurrent,
            'cursor-default opacity-60': isDisabled,
            'flex items-center gap-2': isChargeback,
          },
          customClass
        )}
        id={`operation-item-${id}`}
        onClick={(e) => {
          e.stopPropagation();
          onSelect(id);
        }}>
        {isChargeback && (
          <Checkbox
            id={value}
            checked={isSelected}
            disabled={isCheckboxDisabled}
            onChange={(e) => {
              e.persist();
              e.nativeEvent.stopImmediatePropagation();
              e.stopPropagation();
              onCheckboxChange?.(id, e.target.checked);
            }}
          />
        )}
        <ConditionalWrapper
          condition={isChargeback}
          wrap={(children) => <div className='min-w-0'>{children}</div>}>
          {!isChargeback && (
            <div className='mb-2 flex flex-wrap items-center justify-between gap-2'>
              <span className='text-m-semi-bold capitalize'>
                {operationType}
              </span>
              <span className='text-text-secondary'>{updatedDate}</span>
            </div>
          )}
          <div className='font-heading text-heading-sm mb-2 flex gap-1'>
            <span>{amount}</span>
            <span>{currency}</span>
          </div>
          <div className='mb-2 flex gap-2'>
            <LabelStatus text={status} status={Mapper.getStatus(status)} />

            {recurring ? (
              <LabelStatus
                text={recurring}
                status={Mapper.getStatus(recurring)}
                onClick={(e) => {
                  e.stopPropagation();

                  if (recurring === 'retry operation') {
                    return;
                  }

                  if (onStatusClick) {
                    onStatusClick(id);
                  }
                }}
              />
            ) : null}

            {Boolean(fraud) && (
              <LabelStatus text='fraud' status={Mapper.getStatus('fraud')} />
            )}
            {Boolean(chargeback) && (
              <LabelStatus
                text='chargeback'
                status={Mapper.getStatus('chargeback')}
              />
            )}
          </div>
          <div className='flex items-center gap-1'>
            <span>{label}:</span>
            <span className='text-brand-main overflow-hidden text-ellipsis'>
              {isChargeback ? value : id}
            </span>
            <CopyToClipboard text={value} />
          </div>
        </ConditionalWrapper>
      </div>
    );
  }
}

export default withRouter(addTranslation(OperationItem));
